import {
  Logo,
  Button,
  Canvas,
  SceneContainer,
  ButtonContainer
} from './Overview.styles';

import {
  type ISDK,
  Initialize,
  type AvatarData
} from '@tbwax_dev/mcd-family-sdk';

import Scene from './Scene';
import { Theme } from '@/theme';
import type { Vector2 } from 'three';
import { connect } from 'react-redux';
import { useLoader } from '@CS/hooks';
import { isDEV } from '@/utils/Location';
import { Images } from '@/assets/images';

import EditButton from '@/components/EditButton';
import AvatarLabel from '@/components/AvatarLabel';
import { useRef, useState, useEffect } from 'react';
import { getCampaign } from '@/redux/campaign/selectors';
import type { CampaignState } from '@/redux/campaign/types';
import { WelcomePopup } from '@/components/Popups/WelcomePopup';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { AvatarCreatePopup } from '@/components/Popups/AvatarCreatePopup';
import type { RootState } from '@/redux/types';
import type { UserState } from '@/redux/user/types';

type OverviewProps = {
  campaign: CampaignState;
  data: UserState;
};

interface FamilyMember {
  memberData: AvatarData;
  avatar: GLTF;
}

const Overview = ({ data, campaign }: OverviewProps) => {
  const [hasAvatars, setHasAvatars] = useState<boolean | undefined>(void 0);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [showAvatarCreate, setShowAvatarCreate] = useState(false);

  const [showWelcome, setShowWelcome] = useState(false);
  const [labels, setLabels] = useState<Vector2[]>([]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);

  const names = useRef<string[]>([]);
  const scene = useRef<Scene>(null);

  const loader = useLoader(loading, { color: Theme.color.orange });

  const userEmail = data?.email || '';
  const popupsKey = `popups_${userEmail}`;
  const popupsShown = localStorage.getItem(popupsKey) === 'true';

  const createAvatar = () =>
    (location.href = `${(campaign.publicData as Record<string, string>).avatarCreator}`);

  const handleClosePopup = () => {
    if (showWelcome) {
      setShowWelcome(false);
      setShowAvatarCreate(true);
    } else if (showAvatarCreate) {
      setShowAvatarCreate(false);
      localStorage.setItem(popupsKey, 'true');
    }
  };

  const loadFamilyData = async (sdk: ISDK) => {

    try {
      const familyData = await sdk.getFamilyData();

      const member = familyData!.avatars;
      const avatars = await sdk.getFamilyAvatars();

      const membersData = avatars && avatars
        .map((avatar, a) => ({ avatar, memberData: member[a] }))
        .filter((item) => item.memberData);

      setFamilyMembers(membersData as FamilyMember[]);
      // sdk.disposeAvatarManager();
      setHasAvatars(true);
      setLoading(false);
    } catch (error) {
      if (error instanceof Error && error.cause === 404) {
        setHasAvatars(false);
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    const { length } = familyMembers;
    if (!length || !hasAvatars || !canvas.current || labels.length) return;

    scene.current = new Scene(canvas.current);

    familyMembers.forEach(({ avatar, memberData: { age, name } }, m) => {
      scene.current!.addAvatar(avatar, age, m, length);
      names.current.push(name);
    });

    setLabels(scene.current.labels);
  }, [canvas.current, familyMembers, hasAvatars]);

  useEffect(() => {
    if (!data) return;

    if (hasAvatars === false && !popupsShown && !showWelcome && !showAvatarCreate) {
      setShowAvatarCreate(false);
      setShowWelcome(true);
    } else if (popupsShown || hasAvatars !== false) {
      setShowAvatarCreate(false);
      setShowWelcome(false);
    }
  }, [data, hasAvatars, showWelcome, showAvatarCreate, popupsShown]);

  useEffect(() => {
    const casClientId = import.meta.env.VITE_CAS_CLIENT_ID;

    Initialize({
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiKey: import.meta.env.VITE_API_KEY,
      onInitError: console.error,
      onInit: loadFamilyData,
      automaticLogin: true,
      isDEV: isDEV(),
      casClientId
    });

    // return () => scene.current?.dispose();
  }, []);

  if (loading || !data)
    return (
      <SceneContainer>
        <Logo alt="Family Mode" src={Images.Logo} />
        {loader}
      </SceneContainer>
    );

  if (!hasAvatars) {
    return (
      <>
        <SceneContainer>
          <Logo alt="Family Mode" src={Images.Logo} />

          <ButtonContainer>
            <Button onClick={createAvatar}>Maak je avatar</Button>
          </ButtonContainer>
        </SceneContainer>

        {!popupsShown && (
          <>
            {showWelcome && <WelcomePopup onClose={handleClosePopup} />}
            {showAvatarCreate && (
              <AvatarCreatePopup onClose={handleClosePopup} />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <SceneContainer>
      <Logo alt="Family Mode" src={Images.Logo} />
      <Canvas ref={canvas} />

      {labels.map(({ x, y }, n) => (
        <AvatarLabel name={names.current[n]} left={x} top={y} key={n} />
      ))}

      <EditButton onClick={createAvatar} />
    </SceneContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: state.user,
  campaign: getCampaign(state)
});

export default connect(mapStateToProps)(Overview);
